'use strict';

angular.module('ecrd.states')
    .provider('FormStatesService', function() {
        var provider = this;

        var _states = {};
        var _lock = null;

        provider.setStates = function(states) {
            _states = states;
        };

        provider.setLock = function(lockInfo) {
            _lock = lockInfo;
        };

        this.$get = FormStatesService;

        FormStatesService.$inject = [
            '$log',
            '$q',
            'AccountManager',
            'CrfService',
            'Event',
            'Modal',
            'RecordDataService',
            'RecordMetadataService',
            'RecordsManagerService',
            'RecordStatesService',
            'RecordValidationService',
            'ShareCrfSentry',
        ];

        function FormStatesService(
            $log,
            $q,
            AccountManager,
            CrfService,
            Event,
            Modal,
            RecordDataService,
            RecordMetadataService,
            RecordsManagerService,
            RecordStatesService,
            RecordValidationService,
            ShareCrfSentry
        ) {
            function hasLock() {
                return _lock !== null;
            }

            function getAvailableStates() {
                return _states;
            }

            function isAvailable() {
                return Object.keys(getAvailableStates()).length > 0 || hasLock();
            }

            function isLocked(formId, listIndices) {
                return hasLock() && RecordMetadataService.isFormLocked(formId, listIndices);
            }

            function getFormPath(formId, listIndices) {
                var namedPath = RecordDataService.getElementFullNamedPath(formId, listIndices);

                var formPath = [];

                namedPath.forEach(function(item, index) {
                    if (typeof item === 'number') {
                        // Es un índice de lista, hay que convertirlo a ID
                        var listName = namedPath[index - 1];
                        var listInstance = CrfService.getElementByName(listName);
                        var listId = listInstance.getId();

                        var itemId = RecordDataService.getListElementId(listId, item, listIndices);

                        // Y se añade a la componente con el nombre de la lista
                        formPath[formPath.length - 1] = listName + '(' + itemId + ')';
                    }
                    else {
                        formPath.push(item);
                    }
                });

                return formPath;
            }

            function _updateState(stateId, formId, listIndices) {
                var currentValue = RecordMetadataService.hasFormState(stateId, formId, listIndices);
                var newValue = !currentValue;

                var stateInfo = getAvailableStates()[stateId];
                var needsSignature = !currentValue && stateInfo.sign;
                var promise;

                var updateStateSpan = null; 
                var updateStateSpanData = {
                    name: 'Update Form State',
                    op: 'http.client',
                    tags: {
                        'sharecrf.state.form.id': formId,
                        'sharecrf.state.id': stateId,
                        'sharecrf.state.name': stateInfo.name,
                        'sharecrf.state.current_state': currentValue,
                        'sharecrf.state.target_state': newValue,

                    },
                    asParent: true,

                };

                if (needsSignature) {
                    // Confirmation modal
                    promise = Modal.open({
                        templateUrl: 'html/entity/modal-signature-needed.html',
                        controller: 'EntityModalSignatureNeededController',
                        controllerAs: 'modal',
                        onlyActions: true,
                        data: {
                            message: stateInfo.signMessage,
                        },
                    }).closePromise; // Modal result, 'Confirm' button pressed
                } else {
                    promise = $q.resolve(true);
                }

                var params = {
                    context: 'form',
                    state: stateId,
                    enabled: newValue,
                };

                return promise
                    .then(function(password) {
                        updateStateSpan = ShareCrfSentry.startTransaction(updateStateSpanData);

                        var formUid = RecordDataService.getElementUID(formId, listIndices);
                        if (Array.isArray(formUid)) {
                            formUid = formUid.join('.');
                        }

                        $log.debug('Set form state ' + stateId + ' to', newValue, formId, listIndices, formUid);

                        params.form = formUid;
                        params.calculateQueries = true;

                        if (needsSignature) {
                            params.password = password;
                        }

                        return RecordsManagerService.setFormState(params);
                    })
                    .catch(function(error) {
                        // Queries que se van a abrir al hacer el update
                        if (error.code === 'queries-to-create') {
                            $log.debug('Queries to create', error.info.queriesToCreate);
                            var queryCandidates = error.info.queriesToCreate;

                            var queries = queryCandidates.map(function(query) {
                                var fieldPath = RecordDataService.getElementFullLabeledPathByUID(query.fieldUId);

                                return {
                                    label: fieldPath.join(' > '),
                                    text: query.text,
                                };
                            });

                            return Modal.open({
                                template: 'modules/ecrd/components/rules/queries/content.html',
                                controller: 'RulesActionCreateQueriesController',
                                controllerAs: '$ctrl',
                                data: {
                                    queries: queries,
                                },
                                onlyActions: true,
                            })
                                .closePromise
                                .then(function() {
                                    // Volvemos a reintentar el update, quitando la comprobación de queries
                                    delete params.calculateQueries;

                                    // Restart span
                                    updateStateSpan = ShareCrfSentry.startTransaction(updateStateSpanData);

                                    return RecordsManagerService.setFormState(params);
                                });
                        }

                        if (error !== Modal.DISMISSED) {
                            $log.error(error, 'modal queries to create on form completion');
                        }

                        throw error;
                    })
                    .then(function(record) {
                        ShareCrfSentry.setTransaction('toggle-form-state', updateStateSpan);
                        $log.debug('Updated metadata from SDK', record.metadata);

                        // reload metadata
                        RecordMetadataService.reset();
                        RecordMetadataService.load(record.metadata);

                        Event.broadcast('EntityFormMetadataUpdateEnd', [stateId, formId, listIndices, newValue]);

                        return newValue;
                    });
            }

            function _updateLock(formId, listIndices) {
                var currentValue = isLocked(formId, listIndices);
                var newValue = !currentValue;
                var needsSignature = !!_lock.sign && !currentValue;

                var updateLockSpan = null;
                var updateLockSpanData = {
                    name: 'Update Form Lock',
                    tags: {
                        'sharecrf.form.id': formId,
                        'sharecrf.lock.current_state': currentValue,
                        'sharecrf.lock.target_state': newValue,
                    },
                    asParent: true,
                };

                var promise;

                if (needsSignature) {
                    promise = Modal.open({
                        templateUrl: 'html/entity/modal-signature-needed.html',
                        controller: 'EntityModalSignatureNeededController',
                        controllerAs: 'modal',
                        onlyActions: true,
                    }).closePromise;
                } else {
                    promise = $q.resolve(null);
                }

                return promise
                    .then(function(password) {
                        updateLockSpan = ShareCrfSentry.startTransaction(updateLockSpanData);

                        var formUid = RecordDataService.getElementUID(formId, listIndices);
                        if (Array.isArray(formUid)) {
                            formUid = formUid.join('.');
                        }
                        $log.debug('Lock form to', !!newValue, formId, listIndices, formUid);

                        var params = {
                            context: 'form',
                            state: 'locked',
                            enabled: newValue,
                            form: formUid,
                            calculateQueries: true,
                        };

                        if (password) {
                            params.password = password;
                        }

                        return RecordsManagerService.setFormLock(params);
                    })
                    .then(function(record) {
                        $log.debug('Updated metadata from SDK', record.metadata);

                        // reload metadata
                        RecordMetadataService.reset();
                        RecordMetadataService.load(record.metadata);

                        ShareCrfSentry.setTransaction('toggle-form-lock', updateLockSpan);

                        // Reload menu
                        Event.broadcast('EntityFormMetadataUpdateEnd', ['lock', formId, listIndices, newValue]);

                        return newValue;
                    });
            }

            // Obtiene solamente la información de los estados habilitados
            function getEnabledStates(formId, listIndices) {
                var allStates = getAvailableStates();

                var enabledStates = {};
                angular.forEach(allStates, function(stateInfo, stateId) {
                    if (RecordMetadataService.hasFormState(stateId, formId, listIndices)) {
                        enabledStates[stateId] = stateInfo;
                    }
                });

                return enabledStates;
            }

            function getDisabledStates(formId, listIndices) {
                var allStates = getAvailableStates();

                var disabledStates = {};
                angular.forEach(allStates, function(stateInfo, stateId) {
                    var stateMetadata = RecordMetadataService.getFormState(stateId, formId, listIndices);
                    if (angular.isObject(stateMetadata) && stateMetadata.value === false) {
                        disabledStates[stateId] = stateInfo;
                    }
                });

                return disabledStates;
            }

            /**
             * Obtiene información sobre el estado de edición de cada estado
             *
             * @param  {integer}     formId      ID del formulario
             * @param  {ListIndices} listIndices Índices de las listas a las que pertenece el formulario
             *
             * @return {object}                  Objeto indexado por ID de estado, especificando si es editable o no, y
             *                                   la razón en caso de no serlo
             */
            function getEditionInfo(formId, listIndices) {
                var editionInfo = {};

                var available = isAvailable(formId);
                var isRecordValid = RecordValidationService.isRecordValid();
                var hasGlobalLock = RecordStatesService.isLocked();
                var formLocked = isLocked(formId, listIndices);

                // Motivo asociado, solamente uno en este orden
                var reason = null;
                if (!available) {
                    reason = 'unavailable';
                } else if (!isRecordValid) {
                    reason = 'validation';
                } else if (hasGlobalLock) {
                    reason = 'global-lock';
                }

                var permission;

                if (hasLock()) {
                    var lockReason = reason;
                    permission = formLocked ? 'unlock records forms' : 'lock records forms';
                    if (!AccountManager.hasPermission(permission)) {
                        lockReason = 'permission';
                    }

                    editionInfo.lock = {
                        editable: !lockReason,
                        reason: lockReason,
                    };
                }

                // Y para el resto de estados se requiere, además, que el formulario no esté bloqueado
                if (!reason && formLocked) {
                    reason = 'form-lock';
                }

                var enabledStates = getEnabledStates(formId, listIndices);

                angular.forEach(getAvailableStates(), function(stateInfo, stateId) {
                    var stateReason = reason;
                    var hasState = angular.isDefined(enabledStates[stateId]);
                    permission = hasState ? 'unset forms state ' + stateId : 'set forms state ' + stateId;
                    if (!AccountManager.hasPermission(permission)) {
                        stateReason = 'permission';
                    }

                    editionInfo[stateId] = {
                        editable: !stateReason,
                        reason: stateReason,
                    };
                });

                return editionInfo;
            }

            function hasAnyEditableState() {
                var hasAny = false;

                var availableStates = getAvailableStates();
                for (var stateId in availableStates) {
                    hasAny = hasAny || (
                        AccountManager.hasAtLeastOnePermission([
                            'set forms state ' + stateId,
                            'unset forms state ' + stateId,
                        ])
                    );
                }

                return hasAny;
            }

            function hasEditableLock() {
                return hasLock()
                    && AccountManager.hasAtLeastOnePermission(['lock records forms', 'unlock records forms']);
            }

            function getAuditChanges(formId, listIndices, stateId) {
                return RecordsManagerService.getAuditChanges({
                    formPath: getFormPath(formId, listIndices),
                    stateId: stateId,
                });
            }

            return {
                isAvailable: isAvailable,
                hasAnyEditableAction: function() {
                    return hasEditableLock() || hasAnyEditableState();
                },
                getAvailableStates: getAvailableStates,
                getEditionInfo: getEditionInfo,
                getEnabledStates: getEnabledStates,
                getDisabledStates: getDisabledStates,
                getEditableStates: function(formId) {
                    var allStates = getAvailableStates();
                    var editableStates = {};

                    angular.forEach(allStates, function(stateInfo, stateId) {
                        if (AccountManager.hasAtLeastOnePermission([
                            'set forms state ' + stateId,
                            'unset forms state ' + stateId,
                        ])) {
                            editableStates[stateId] = stateInfo;
                        }
                    });

                    return editableStates;
                },
                hasAnyEditableState: hasAnyEditableState,
                toggleState: function(stateId, formId, listIndices) {
                    var stateInfo = getAvailableStates()[stateId];
                    if (!stateInfo) {
                        return $q.reject('form-state-' + stateId + '-not-available');
                    }

                    return Event.broadcast('EntityFormMetadataUpdateStart', [stateId, formId, listIndices])
                        .then(function(event) {
                            return event.defaultPrevented ? 'cancel' : _updateState(stateId, formId, listIndices);
                        });
                },
                hasLock: hasLock,
                hasEditableLock: hasEditableLock,
                isLocked: isLocked,
                toggleLock: function(formId, listIndices) {
                    return Event.broadcast('EntityFormMetadataUpdateStart', ['lock', formId, listIndices])
                        .then(function(event) {
                            return event.defaultPrevented ? 'cancel' : _updateLock(formId, listIndices);
                        });
                },
                getAuditChanges: getAuditChanges,
            };
        }
    });
